import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatDate, formatEuro, clienteNome, LoadingSpinner } from '../components/shared';

function StepBadge({ n, active, done }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600,
      background: done ? 'var(--c-green)' : active ? 'var(--accent)' : 'var(--bg)',
      color: done || active ? 'white' : 'var(--text-faint)',
      border: `2px solid ${done ? 'var(--c-green)' : active ? 'var(--accent)' : 'var(--border-strong)'}`,
    }}>
      {done ? '✓' : n}
    </div>
  );
}

export default function ImportRateazione({ addToast }) {
  const navigate    = useNavigate();
  const fileRef     = useRef();
  const [step, setStep]           = useState(1); // 1=upload 2=anteprima 3=fatto
  const [loading, setLoading]     = useState(false);
  const [parsed, setParsed]       = useState(null);   // dati estratti dal PDF
  const [clienti, setClienti]     = useState([]);
  const [idCliente, setIdCliente] = useState('');
  const [descBase, setDescBase]   = useState('');
  const [rate, setRate]           = useState([]);      // rate selezionate
  const [risultato, setRisultato] = useState(null);

  useEffect(() => {
    api.getClienti({ limit: 300 }).then(d => setClienti(d.rows));
  }, []);

  // ── Step 1: Upload PDF ────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      addToast('Seleziona un file PDF', 'error'); return;
    }
    setLoading(true);
    try {
      const data = await api.parseRateazione(file);
      setParsed(data);
      setRate(data.rate.map(r => ({ ...r, selezionata: true })));
      setDescBase('Piano rateazione Agenzia Entrate');
      // Pre-seleziona cliente se trovato
      if (data.cliente) setIdCliente(String(data.cliente.id));
      setStep(2);
    } catch(e) { addToast('Errore lettura PDF: ' + e.message, 'error'); }
    finally { setLoading(false); }
  };

  const toggleRata = (idx) => setRate(prev => prev.map((r, i) => i===idx ? {...r, selezionata:!r.selezionata} : r));
  const rateSelezionate = rate.filter(r => r.selezionata);
  const totaleSelezionato = rateSelezionate.reduce((s, r) => s + r.importo_totale, 0);

  // ── Step 2: Conferma e importa ────────────────────────────────────────────
  const handleImport = async () => {
    if (!rateSelezionate.length) { addToast('Seleziona almeno una rata', 'error'); return; }
    setLoading(true);
    try {
      const res = await api.confermaRateazione({
        id_cliente:       idCliente || null,
        rate:             rateSelezionate,
        descrizione_base: descBase,
      });
      setRisultato(res);
      setStep(3);
      addToast(`✅ Create ${res.inserite} scadenze`, 'success');
    } catch(e) { addToast(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const clienteSelezionato = clienti.find(c => String(c.id) === String(idCliente));

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <button className="btn btn-sm" onClick={() => navigate('/scadenze')} style={{ marginBottom:8 }}>← Scadenze</button>
          <div className="page-title">Import Piano Rateazione</div>
          <div className="page-subtitle">Crea scadenze automaticamente da PDF Agenzia Entrate</div>
        </div>
      </div>

      {/* Stepper */}
      <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:28 }}>
        {[['Carica PDF','1'],['Verifica e assegna','2'],['Importato','3']].map(([label, n], i) => (
          <React.Fragment key={n}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <StepBadge n={n} active={step===i+1} done={step>i+1} />
              <span style={{ fontSize:13, fontWeight:step===i+1?500:400, color:step===i+1?'var(--accent)':step>i+1?'var(--c-green)':'var(--text-faint)' }}>
                {label}
              </span>
            </div>
            {i < 2 && <div style={{ flex:1, height:1, background:'var(--border)', margin:'0 12px', minWidth:40 }} />}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1: UPLOAD ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card" style={{ maxWidth:560 }}>
          <div className="card-header"><span className="card-title">Carica il PDF del piano di rateazione</span></div>
          <div className="card-body">
            <div style={{
              border:'2px dashed var(--border-strong)', borderRadius:'var(--radius)',
              padding:'40px 24px', textAlign:'center', cursor:'pointer',
              background:'var(--bg)', transition:'border-color 0.15s',
            }}
              onClick={() => fileRef.current.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='var(--accent)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor=''; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor=''; const f=e.dataTransfer.files[0]; if(f) handleFile(f); }}
            >
              <input ref={fileRef} type="file" accept=".pdf" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])} />
              {loading ? (
                <div>
                  <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
                  <div style={{ fontSize:14, fontWeight:500 }}>Analisi PDF in corso…</div>
                  <div style={{ fontSize:12, color:'var(--text-faint)', marginTop:4 }}>Estrazione rate e dati cliente</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:48, marginBottom:12 }}>📄</div>
                  <div style={{ fontSize:15, fontWeight:500, color:'var(--text)' }}>Trascina il PDF qui</div>
                  <div style={{ fontSize:13, color:'var(--text-faint)', marginTop:6 }}>oppure clicca per selezionare</div>
                  <div style={{ fontSize:11, color:'var(--text-faint)', marginTop:10, padding:'6px 12px',
                    background:'var(--surface)', borderRadius:'var(--radius-sm)', display:'inline-block' }}>
                    Supportato: Piano rateazione Agenzia Entrate (F24)
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop:20, padding:'12px 16px', background:'var(--accent-light)',
              borderRadius:'var(--radius-sm)', fontSize:12.5, color:'var(--accent)' }}>
              <strong>Come funziona:</strong> carica il PDF del piano di rateazione scaricato da Agenzia Entrate.
              Il sistema rileva automaticamente cliente, importi e date di ogni rata,
              poi crea una scadenza F24 per ciascuna.
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 2: ANTEPRIMA ─────────────────────────────────────────────── */}
      {step === 2 && parsed && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:20, alignItems:'start' }}>
          {/* Colonna sinistra: rate */}
          <div>
            <div className="card" style={{ marginBottom:16 }}>
              <div className="card-header">
                <span className="card-title">Dati estratti dal PDF</span>
                <button className="btn btn-sm" onClick={() => { setStep(1); setParsed(null); }}>← Ricarica</button>
              </div>
              <div className="card-body" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px 24px' }}>
                {[
                  ['Cliente rilevato', parsed.cliente_nome || '—'],
                  ['Codice Fiscale / P.IVA', parsed.cliente_cf || '—'],
                  ['Totale dilazione', parsed.totale ? formatEuro(parsed.totale) : '—'],
                  ['Numero rate', parsed.n_rate || parsed.rate.length],
                  ['Rate da importare', rateSelezionate.length],
                  ['Importo selezionato', formatEuro(totaleSelezionato)],
                ].map(([l,v]) => (
                  <div key={l}>
                    <div style={{ fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.4px', color:'var(--text-faint)', marginBottom:3 }}>{l}</div>
                    <div style={{ fontSize:13.5, fontWeight:500 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabella rate */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Rate rilevate</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-sm" onClick={() => setRate(r => r.map(x => ({...x, selezionata:true})))}>Tutte</button>
                  <button className="btn btn-sm" onClick={() => setRate(r => r.map(x => ({...x, selezionata:false})))}>Nessuna</button>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th style={{ width:36 }}></th>
                    <th>Rata</th>
                    <th>Data scadenza</th>
                    <th>Capitale</th>
                    <th>Interessi</th>
                    <th>Totale F24</th>
                  </tr></thead>
                  <tbody>
                    {rate.map((r, i) => (
                      <tr key={i} style={{ opacity: r.selezionata ? 1 : 0.4 }}>
                        <td>
                          <input type="checkbox" checked={r.selezionata} onChange={() => toggleRata(i)}
                            style={{ accentColor:'var(--accent)', width:15, height:15, cursor:'pointer' }} />
                        </td>
                        <td style={{ fontWeight:500 }}>#{r.rata}</td>
                        <td style={{ fontSize:12 }}>{formatDate(r.data_scad)}</td>
                        <td style={{ fontFamily:'monospace', fontSize:12 }}>{formatEuro(r.importo_capitale)}</td>
                        <td style={{ fontFamily:'monospace', fontSize:12, color: r.importo_interessi > 0 ? 'var(--c-amber)' : 'var(--text-faint)' }}>
                          {r.importo_interessi > 0 ? formatEuro(r.importo_interessi) : '—'}
                        </td>
                        <td style={{ fontFamily:'monospace', fontSize:13, fontWeight:600 }}>{formatEuro(r.importo_totale)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} style={{ padding:'10px 16px', fontSize:12, fontWeight:500, color:'var(--text-muted)', borderTop:'2px solid var(--border)' }}>
                        Totale selezionato ({rateSelezionate.length} rate)
                      </td>
                      <td style={{ padding:'10px 16px', fontFamily:'monospace', fontSize:13, fontWeight:700, borderTop:'2px solid var(--border)', color:'var(--accent)' }}>
                        {formatEuro(totaleSelezionato)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Colonna destra: configurazione */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Assegna al cliente</span></div>
              <div className="card-body">
                {parsed.cliente ? (
                  <div style={{ marginBottom:12, padding:'10px 14px', background:'var(--c-green-bg)',
                    border:'1px solid var(--c-green)', borderRadius:'var(--radius-sm)', fontSize:13 }}>
                    <div style={{ fontWeight:500, color:'var(--c-green)' }}>✓ Cliente trovato in anagrafica</div>
                    <div style={{ fontSize:12, marginTop:2 }}>{clienteNome(parsed.cliente)}</div>
                  </div>
                ) : (
                  <div style={{ marginBottom:12, padding:'10px 14px', background:'var(--c-amber-bg)',
                    border:'1px solid var(--c-amber)', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--c-amber)' }}>
                    ⚠ Cliente non trovato automaticamente — selezionalo manualmente
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Cliente *</label>
                  <select className="form-control" value={idCliente} onChange={e => setIdCliente(e.target.value)} required>
                    <option value="">— Seleziona cliente —</option>
                    {clienti.map(c => (
                      <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Descrizione scadenze</span></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Base descrizione</label>
                  <input className="form-control" value={descBase} onChange={e => setDescBase(e.target.value)}
                    placeholder="Es. Piano rateazione Agenzia Entrate" />
                  <div style={{ fontSize:11, color:'var(--text-faint)', marginTop:5 }}>
                    Ogni rata verrà denominata:<br/>
                    <em>"{descBase} — Rata 1/{rate.length}"</em>
                  </div>
                </div>
              </div>
            </div>

            <button className="btn btn-primary"
              style={{ padding:'12px', fontSize:14, fontWeight:500, width:'100%' }}
              disabled={loading || !idCliente || !rateSelezionate.length}
              onClick={handleImport}>
              {loading ? 'Importazione…' : `✓ Importa ${rateSelezionate.length} scadenze`}
            </button>

            {!idCliente && (
              <div style={{ fontSize:12, color:'var(--c-red)', textAlign:'center' }}>
                Seleziona prima il cliente
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STEP 3: FATTO ─────────────────────────────────────────────────── */}
      {step === 3 && risultato && (
        <div className="card" style={{ maxWidth:520 }}>
          <div className="card-body" style={{ textAlign:'center', padding:'40px 24px' }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:500, marginBottom:8 }}>
              Importazione completata
            </div>
            <div style={{ fontSize:14, color:'var(--text-muted)', marginBottom:24 }}>
              Sono state create <strong>{risultato.inserite} scadenze F24</strong>
              {clienteSelezionato && <> per <strong>{clienteNome(clienteSelezionato)}</strong></>}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="btn btn-primary" onClick={() => navigate('/scadenze')}>
                Vai alle Scadenze
              </button>
              <button className="btn" onClick={() => { setStep(1); setParsed(null); setRisultato(null); setRate([]); setIdCliente(''); }}>
                Importa altro PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
